import { useSyncExternalStore } from 'react'
const KEY = 'relaii:external-images:v1'
const EVENT = 'relaii:external-images-changed'

function allowed(): boolean {
  try { return window.localStorage.getItem(KEY) === 'allow' } catch { return false }
}
function subscribe(listener: () => void): () => void {
  window.addEventListener('storage', listener)
  window.addEventListener(EVENT, listener)
  return () => { window.removeEventListener('storage', listener); window.removeEventListener(EVENT, listener) }
}
export function useExternalImages(): boolean { return useSyncExternalStore(subscribe, allowed, () => false) }
export function setExternalImages(allow: boolean): void {
  try { window.localStorage.setItem(KEY, allow ? 'allow' : 'ask') } catch { /* Keep the safer default. */ }
  window.dispatchEvent(new Event(EVENT))
}

/** True for an absolute URL that would be fetched from somewhere other than this Space. */
export function isExternalUrl(value: string): boolean {
  try {
    return new URL(value, window.location.href).origin !== window.location.origin
  } catch {
    return true
  }
}

/**
 * The avatar to draw for a person's own profile image.
 *
 * A person's uploaded image is served by this Space and never needs asking.
 * A bot's avatar is different: an admin (for an API key) or a room manager
 * (for a webhook) may set it to any http(s) address, so loading it tells that
 * address which readers saw the bot, and when. A bot avatar that would be
 * fetched from somewhere other than this Space is therefore held back, like a
 * webhook's per-message avatar, until the reader has allowed external images
 * on this device — the initials stand in. An uploaded bot avatar is served
 * here and is always drawn, and so is any human's.
 *
 * Use this wherever a user's `avatar_url` is drawn for someone who might be
 * a bot; `allowExternal` is `useExternalImages()`.
 */
export function userAvatarSrc(
  user: { is_bot?: boolean; avatar_url?: string | null },
  allowExternal: boolean,
): string | null {
  const url = user.avatar_url
  if (url === undefined || url === null || url === '') return null
  if (user.is_bot !== true || allowExternal || !isExternalUrl(url)) return url
  return null
}

/**
 * A plain pasted URL, gated by the same external-origin consent rule as
 * `userAvatarSrc` — for call sites that hold a bare `bot_avatar_url` string
 * rather than a user-shaped object: a webhook or API-key manager view (the
 * webhook row's avatar, `BotAvatarField`'s preview, the API keys page).
 *
 * A same-origin uploaded avatar is always drawn; a pasted external address
 * waits for `allowExternal`, exactly like a bot's `avatar_url` in
 * `userAvatarSrc`.
 */
export function externalImageSrc(url: string | null | undefined, allowExternal: boolean): string | null {
  if (url === undefined || url === null || url === '') return null
  if (allowExternal || !isExternalUrl(url)) return url
  return null
}

/**
 * The avatar to draw for a message.
 *
 * A webhook may name an avatar of its own, and whoever holds the webhook's
 * secret chooses the address — so loading it tells that address which readers
 * opened the room, and when. It is treated like a webhook image block: the
 * initials stand in until the reader has allowed external images on this
 * device. Without one, the sender's own avatar is drawn by `userAvatarSrc`'s
 * rule: a person's is served by this Space, but a bot's may be an external
 * address and waits for the same consent.
 */
export function senderAvatarSrc(
  message: {
    sender_avatar_url?: string | null
    sender: { is_bot?: boolean; avatar_url: string | null }
  },
  allowExternal: boolean,
): string | null {
  const override = message.sender_avatar_url
  if (override === undefined || override === null || override === '') {
    return userAvatarSrc(message.sender, allowExternal)
  }
  if (allowExternal || !isExternalUrl(override)) return override
  return null
}
